import { memo, useCallback, useMemo, useState } from "react";
import { Button, Stack } from "@mui/material";
import { Add } from "@mui/icons-material";
import dayjs, { type Dayjs } from "dayjs";
import { BookTeleconsultoriaDialog } from "../../booking/components/BookTeleconsultoriaDialog";
import type { BookingTarget, RequesterMode } from "../../booking/types";
import { ActivitySectionFrame } from "../../provider/components/ActivitySectionFrame";
import { ProfessionalFilterSelect } from "../../provider/components/ProfessionalFilterSelect";
import { TeleconsultoriaDateFilter } from "../../shared/components/TeleconsultoriaDateFilter";
import { useTeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { isSamePractitionerReference } from "../../utils/actors";
import { useAffiliateTeleconsultorias } from "../hooks/useAffiliateTeleconsultorias";
import { useTemporaryHighlight } from "../hooks/useTemporaryHighlight";
import type { AffiliateTeleconsultoriaRow } from "../services/affiliateTeleconsultoriasService";
import { AffiliateBookedRow } from "./AffiliateBookedRow";
import { BookingPickerDrawer } from "./BookingPickerDrawer";
import { OnlineTeleconsultoresChip } from "./OnlineTeleconsultoresChip";

/**
 * "Teleconsultorias" da AFILIADA num componente só:
 * - foco nas marcadas (um filtro de data; na clínica, também por profissional);
 * - "Marcar teleconsultoria" abre o painel (dia → teleconsultor → horário);
 * - painel e diálogo em sequência (nunca os dois abertos); "Trocar horário" volta ao painel;
 * - depois de marcar, a lista vai para o dia da consulta e destaca a linha nova.
 */
export const AffiliateTeleconsultoriasHub = memo(function AffiliateTeleconsultoriasHub({
  providerName,
  requesterMode,
}: {
  providerName: string;
  requesterMode: RequesterMode;
}) {
  const range = useTeleconsultoriaDateRange("day");
  const [requesterFilter, setRequesterFilter] = useState("");
  /** Muda depois de marcar: recarrega as marcadas (e os horários no painel). */
  const [refreshKey, setRefreshKey] = useState(0);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [bookingTarget, setBookingTarget] = useState<BookingTarget | null>(null);
  const { highlightedId, highlight } = useTemporaryHighlight();
  const isClinicView = requesterMode === "choose";

  const { rows, isLoading, hasError, selfReference, requesterOptions, cancellingId, cancelBooking, reload } =
    useAffiliateTeleconsultorias(refreshKey, {
      requesterMode,
      startDate: range.startDate,
      endDate: range.endDate,
    });
  const visibleRows = useMemo(
    () =>
      requesterFilter
        ? rows.filter((row) => isSamePractitionerReference(row.requesterReference, requesterFilter))
        : rows,
    [rows, requesterFilter],
  );
  const filterOptions = useMemo(
    () => requesterOptions.map((option) => ({ value: option.reference, label: option.display || "Profissional" })),
    [requesterOptions],
  );

  /** Dia em que o painel reabre no "Trocar horário" (o do horário escolhido). */
  const [pickerDate, setPickerDate] = useState<Dayjs | null>(null);

  const openPicker = useCallback(() => {
    setPickerDate(null);
    setIsPickerOpen(true);
  }, []);
  const closePicker = useCallback(() => setIsPickerOpen(false), []);
  // Escolheu horário/"Agora": fecha o painel e abre o diálogo (um overlay por vez).
  const handlePick = useCallback((target: BookingTarget) => {
    setIsPickerOpen(false);
    setPickerDate(target.mode === "slot" ? dayjs(target.start) : dayjs());
    setBookingTarget(target);
  }, []);
  const handleChangeTarget = useCallback(() => {
    setBookingTarget(null);
    setIsPickerOpen(true);
  }, []);
  const closeDialog = useCallback(() => setBookingTarget(null), []);
  const { setDate } = range;
  const handleBooked = useCallback(
    (appointment?: fhir5.Appointment) => {
      setBookingTarget(null);
      setRefreshKey((current) => current + 1);
      // Mostra o resultado na hora: lista no dia da consulta + linha em destaque.
      if (appointment?.start) setDate(dayjs(appointment.start));
      highlight(appointment?.id);
    },
    [setDate, highlight],
  );
  const handleCancel = useCallback(
    (row: AffiliateTeleconsultoriaRow) => void cancelBooking(row),
    [cancelBooking],
  );

  const bookButton = (fullWidth: boolean) => (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={openPicker}
      fullWidth={fullWidth}
      sx={{ textTransform: "none", whiteSpace: "nowrap" }}
    >
      Marcar teleconsultoria
    </Button>
  );

  return (
    <>
      <ActivitySectionFrame
        title={isClinicView ? "Teleconsultorias da clínica" : "Minhas teleconsultorias"}
        subtitle={`Marcadas com os teleconsultores de ${providerName}.`}
        // Desktop: ação no cabeçalho. Celular: botão em largura total no toolbar.
        action={<Stack sx={{ display: { xs: "none", sm: "flex" } }}>{bookButton(false)}</Stack>}
        toolbar={
          <Stack spacing={1.5}>
            <Stack sx={{ display: { xs: "flex", sm: "none" } }}>{bookButton(true)}</Stack>
            <OnlineTeleconsultoresChip onClick={openPicker} />
            <TeleconsultoriaDateFilter range={range} />
            {isClinicView && (
              <ProfessionalFilterSelect options={filterOptions} value={requesterFilter} onChange={setRequesterFilter} />
            )}
          </Stack>
        }
        isLoading={isLoading}
        hasError={hasError}
        errorText="Não foi possível carregar as teleconsultorias marcadas."
        isEmpty={!visibleRows.length}
        emptyText="Nenhuma teleconsultoria marcada neste período."
        emptyAction={bookButton(false)}
        onRefresh={reload}
      >
        {visibleRows.map((row) => (
          <AffiliateBookedRow
            key={row.appointmentId}
            row={row}
            isHost={row.requesterReference === selfReference}
            isCancelling={cancellingId === row.appointmentId}
            isHighlighted={highlightedId === row.appointmentId}
            onCancel={handleCancel}
          />
        ))}
      </ActivitySectionFrame>
      <BookingPickerDrawer
        open={isPickerOpen}
        initialDate={pickerDate ?? range.date}
        providerName={providerName}
        refreshKey={refreshKey}
        onClose={closePicker}
        onBook={handlePick}
      />
      <BookTeleconsultoriaDialog
        target={bookingTarget}
        providerName={providerName}
        requesterMode={requesterMode}
        onClose={closeDialog}
        onBooked={handleBooked}
        onChangeTarget={handleChangeTarget}
      />
    </>
  );
});
